import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Check, Plus, Award, Pencil, ChevronDown, ChevronUp, Star, CloudOff, Cloud, Flame, Eye, Users, CalendarDays, Target, Gift, FileText, Sparkles } from "lucide-react";
import FamilyYearStory from "../components/FamilyYearStory";
import StreakRescueButton from "../components/StreakRescueButton";
import PepTalkRecorder from "../components/PepTalkRecorder";
import DeleteAccount from "../components/DeleteAccount";
import DataExport from "../components/DataExport";
import SoundSettings from "../components/SoundSettings";
import QueueBanner from "../components/QueueBanner";
import Cheers from "../components/Cheers";
import Leaderboard from "../components/Leaderboard";
import GoalBadge from "../components/GoalBadge";
import GiftStickerDialog from "../components/GiftStickerDialog";
import PhotoWall from "../components/PhotoWall";
import { lessonDayStatus, DAYS } from "../lib/lessonDay";
import api from "../lib/api";
import InviteManager from "../components/InviteManager";
import { computeStreak, stepsThisWeek } from "../lib/streak";
import { toast } from "sonner";
import Panda from "../components/Panda";
import { CurveBottom, Cloud as CloudDecor } from "../components/Decor";
import { modules, STEPS } from "../data/mock";
import { useProgress, progressPercent } from "../context/ProgressContext";
import { useAuth } from "../context/AuthContext";
import ReminderSettings from "../components/ReminderSettings";

const WorksheetReview = ({ child, m }) => {
  const ws = m.worksheet;
  const cells = child.worksheets?.[m.id] || {};
  const filled = Object.values(cells).filter((v) => v && v.trim()).length;
  if (filled === 0) return <p className="text-sm text-gray-500 italic">No worksheet answers yet.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr>{ws.columns.map((c) => <th key={c} className="text-left px-2 py-1.5 font-bold text-white" style={{ background: m.button }}>{c}</th>)}</tr></thead>
        <tbody>
          {ws.rows.map((row, r) => (
            <tr key={r} className="odd:bg-white even:bg-[#f7fdfe]">
              {ws.columns.map((_, c) => (
                <td key={c} className="px-2 py-1.5 border-b border-[#e9f9fd] align-top">
                  {row[c] !== undefined ? <span className="font-semibold text-[#0c4a6e]">{row[c]}</span> : <span className={cells[`${r}-${c}`] ? "text-gray-800" : "text-gray-300"}>{cells[`${r}-${c}`] || "-"}</span>}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const GOAL_OPTIONS = [1, 2, 3, 4, 5, 6, 7].map((n) => ({ value: n, label: n === 1 ? "1 step a week" : `${n} steps a week` }));

// Small progress ring: lesson steps done this week vs the weekly goal.
const GoalRing = ({ done, goal, childId }) => {
  const pct = Math.min(100, Math.round((done / goal) * 100));
  const met = done >= goal;
  const r = 14, c = 2 * Math.PI * r;
  return (
    <span className={`inline-flex items-center gap-2 rounded-full pl-1 pr-3 py-0.5 text-xs font-bold ${met ? "bg-[#63c79a] text-white" : "bg-[#e9f9fd] text-[#0c4a6e]"}`} data-testid={`goal-progress-${childId}`} data-done={done} data-goal={goal}>
      <svg width="34" height="34" viewBox="0 0 34 34" className="-rotate-90">
        <circle cx="17" cy="17" r={r} fill="none" stroke={met ? "rgba(255,255,255,0.35)" : "#ffffff"} strokeWidth="4" />
        <circle cx="17" cy="17" r={r} fill="none" stroke={met ? "#ffffff" : "#63c79a"} strokeWidth="4" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c - (c * pct) / 100} className="transition-[stroke-dashoffset] duration-700" />
      </svg>
      {met ? <><Check size={12} /> Goal reached - {done} of {goal} steps</> : `${done} of ${goal} steps this week`}
    </span>
  );
};

const ChildCard = ({ child, isActive, onActivate, readOnly = false, siblings = [] }) => {
  const [openModule, setOpenModule] = useState(null);
  const [giftOpen, setGiftOpen] = useState(false);
  const weekSteps = stepsThisWeek(child.stepLog || {});
  const { updateChild } = useProgress();
  const { user } = useAuth();
  const ld = lessonDayStatus(child.lessonDay, child.activity || []);
  const shared = !readOnly && user && child.ownerId && child.ownerId !== user.id;
  const certs = modules.filter((m) => progressPercent(child, m.id) === 100);
  const quizzes = modules.filter((m) => child.completed?.[m.id]?.quiz !== undefined);
  const streak = computeStreak(child.activity || []);
  const quizAvg = quizzes.length ? Math.round((quizzes.reduce((a, m) => a + child.completed[m.id].quiz, 0) / (quizzes.length * 5)) * 100) : null;

  return (
    <div id={`child-${child.id}`} className={`bg-white rounded-3xl shadow-md overflow-hidden border-4 scroll-mt-28 ${isActive ? "border-[#63c79a]" : "border-transparent"}`} data-testid={`child-card-${child.id}`}>
      <div className="p-6 flex flex-col sm:flex-row items-center gap-6 bg-[#f7fdfe]">
        <Panda size={110} accessory={child.panda.accessory} scarf={child.panda.scarf} item="coin" />
        <div className="flex-1 text-center sm:text-left">
          <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2">
            <h2 className="font-logo text-3xl text-[#0c4a6e]">{child.panda.name}</h2>
            {readOnly ? (
              <span className="text-xs font-bold bg-[#f3e8ff] text-[#7d5fb2] rounded-full px-3 py-1 inline-flex items-center gap-1"><Eye size={12} /> {child.ownerName}'s family &middot; read-only</span>
            ) : isActive ? (
              <span className="text-xs font-bold bg-[#63c79a] text-white rounded-full px-3 py-1">Learning now</span>
            ) : (
              <button onClick={onActivate} className="text-xs font-bold bg-white border-2 border-[#1b8bc9] text-[#1b8bc9] rounded-full px-3 py-1 hover:bg-[#e9f9fd]" data-testid={`activate-${child.id}`}>Switch to {child.panda.name}</button>
            )}
          </div>
          <div className="mt-3 flex flex-wrap justify-center sm:justify-start gap-3 text-sm">
            <span className="inline-flex items-center gap-1 bg-white rounded-full px-3 py-1 font-bold text-[#2e8b57] shadow-sm"><Star size={14} fill="#2e8b57" /> {child.bamboo} bamboo</span>
            <span className="inline-flex items-center gap-1 bg-white rounded-full px-3 py-1 font-bold text-[#8a6d00] shadow-sm"><Award size={14} /> {certs.length} certificate{certs.length === 1 ? "" : "s"}</span>
            {quizAvg !== null && <span className="inline-flex items-center gap-1 bg-white rounded-full px-3 py-1 font-bold text-[#7d5fb2] shadow-sm">Quiz avg {quizAvg}%</span>}
            <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 font-bold shadow-sm ${streak.activeThisWeek ? "bg-[#f7931e] text-white" : "bg-white text-[#8a6d00]"}`} data-testid={`streak-${child.id}`}>
              <Flame size={14} fill={streak.activeThisWeek ? "#fff" : "none"} /> {streak.weeks}-week streak{streak.atRisk ? " (at risk)" : streak.activeThisWeek ? "" : ""}
            </span>
            {shared && <span className="inline-flex items-center gap-1 bg-[#f3e8ff] rounded-full px-3 py-1 font-bold text-[#7d5fb2] shadow-sm" data-testid={`shared-${child.id}`}><Users size={14} /> Shared family</span>}
          </div>

          {/* Weekly lesson day */}
          <div className="mt-3 flex flex-wrap items-center justify-center sm:justify-start gap-2 text-sm" data-testid={`lesson-day-${child.id}`}>
            <CalendarDays size={16} className="text-[#1b8bc9]" />
            {readOnly ? (
              <span className="text-gray-600">{ld ? `${ld.dayName}s - ${ld.label}` : "No lesson day set"}</span>
            ) : (
              <>
                <span className="text-gray-600">Lesson day:</span>
                <select
                  value={child.lessonDay ?? ""}
                  onChange={(e) => { const v = e.target.value === "" ? null : Number(e.target.value); updateChild(child.id, { lessonDay: v }); toast.success(v === null ? "Lesson day cleared" : `${child.panda.name}'s lesson day is ${DAYS[v]}`); }}
                  className="h-8 rounded-full border-2 border-[#c9ecf5] bg-white px-3 text-sm font-bold text-[#0c4a6e] focus:outline-none focus:border-[#1b8bc9]"
                  data-testid={`lesson-day-select-${child.id}`}
                >
                  <option value="">Not set</option>
                  {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
                </select>
              </>
            )}
            {ld && (
              <span className={`rounded-full px-3 py-1 text-xs font-bold ${ld.isToday && !ld.doneToday ? "bg-[#e5356f] text-white" : ld.doneToday ? "bg-[#63c79a] text-white" : "bg-[#e9f9fd] text-[#0c4a6e]"}`} data-testid={`lesson-countdown-${child.id}`}>
                {ld.label}
              </span>
            )}
          </div>

          {/* Weekly steps goal */}
          <div className="mt-3 flex flex-wrap items-center justify-center sm:justify-start gap-2 text-sm" data-testid={`weekly-goal-${child.id}`}>
            <Target size={16} className="text-[#63c79a]" />
            {readOnly ? (
              <span className="text-gray-600">{child.weeklyGoal ? `Goal: ${child.weeklyGoal} step${child.weeklyGoal === 1 ? "" : "s"} a week` : "No weekly goal set"}</span>
            ) : (
              <>
                <span className="text-gray-600">Weekly goal:</span>
                <select
                  value={child.weeklyGoal ?? ""}
                  onChange={(e) => { const v = e.target.value === "" ? null : Number(e.target.value); updateChild(child.id, { weeklyGoal: v }); toast.success(v === null ? "Weekly goal cleared" : `${child.panda.name}'s goal: ${v} step${v === 1 ? "" : "s"} a week`); }}
                  className="h-8 rounded-full border-2 border-[#c9ecf5] bg-white px-3 text-sm font-bold text-[#0c4a6e] focus:outline-none focus:border-[#63c79a]"
                  data-testid={`weekly-goal-select-${child.id}`}
                >
                  <option value="">Not set</option>
                  {GOAL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </>
            )}
            {child.weeklyGoal && <GoalRing done={weekSteps} goal={child.weeklyGoal} childId={child.id} />}
            <GoalBadge child={child} showProgress />
          </div>
          {!readOnly && child.ownerId && siblings.length > 0 && (
            <div className="mt-3 flex justify-center sm:justify-start">
              <button type="button" onClick={() => setGiftOpen(true)} className="inline-flex items-center gap-1.5 text-xs font-bold text-[#7d5fb2] bg-[#f3e8ff] hover:bg-[#7d5fb2] hover:text-white rounded-full px-3 py-1.5 transition-colors" data-testid={`gift-button-${child.id}`}><Gift size={14} /> Gift a sticker</button>
              <GiftStickerDialog giver={child} siblings={siblings} open={giftOpen} onOpenChange={setGiftOpen} />
            </div>
          )}
        </div>
        <div className="flex flex-col items-center sm:items-end gap-2">
          {!readOnly && <Link to={`/panda?child=${child.id}`} className="inline-flex items-center gap-1 text-sm font-bold text-[#1b8bc9] hover:text-[#e5356f]" data-testid={`edit-${child.id}`}><Pencil size={14} /> Edit</Link>}
          {child.ownerId && <Link to={`/report-card/${child.id}`} className="inline-flex items-center gap-1 text-xs font-bold text-[#0c4a6e] bg-[#e9f9fd] hover:bg-[#1b8bc9] hover:text-white rounded-full px-3 py-1.5 transition-colors" data-testid={`report-card-link-${child.id}`}><FileText size={13} /> Report card</Link>}
        </div>
      </div>

      <div className="divide-y divide-[#e9f9fd]">
        {modules.map((m) => {
          const mod = child.completed?.[m.id] || {};
          const pct = progressPercent(child, m.id);
          const open = openModule === m.id;
          return (
            <div key={m.id}>
              <div className="px-6 py-4 flex flex-col md:flex-row md:items-center gap-3">
                <div className="flex items-center gap-3 md:w-[300px]">
                  <span className="w-9 h-9 rounded-full flex items-center justify-center font-logo text-white text-lg flex-shrink-0" style={{ background: m.color }}>{m.num}</span>
                  <div className="min-w-0">
                    <p className="font-bold text-[#0c4a6e] truncate">{m.short} <span className="text-gray-400 font-normal text-xs">Ages {m.ages}</span></p>
                    <p className="text-xs text-gray-500">{pct}% complete</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-1" aria-label="Steps">
                  {STEPS.map((s) => {
                    const done = mod[s.id] !== undefined && mod[s.id] !== false;
                    return (
                      <span key={s.id} title={s.label} className={`h-7 flex-1 max-w-[64px] rounded-full text-[10px] font-bold flex items-center justify-center ${done ? "text-white" : "bg-[#e9f9fd] text-gray-400"}`} style={done ? { background: m.button } : {}}>
                        {done ? <Check size={14} /> : s.label.slice(0, 4)}
                      </span>
                    );
                  })}
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <span className={`font-bold ${mod.quiz !== undefined ? "text-[#2e8b57]" : "text-gray-400"}`}>Quiz {mod.quiz !== undefined ? `${mod.quiz}/5` : "-"}</span>
                  {pct === 100 && (
                    <Link to={`/certificate/${m.slug}?child=${child.id}`} className="inline-flex items-center gap-1 font-bold text-[#8a6d00] bg-[#fff8e1] rounded-full px-3 py-1 hover:bg-[#f3c932]/40" data-testid={`cert-link-${child.id}-${m.id}`}><Award size={14} /> Certificate</Link>
                  )}
                  <button onClick={() => setOpenModule(open ? null : m.id)} className="inline-flex items-center gap-1 font-bold text-[#1b8bc9] hover:text-[#e5356f]" data-testid={`review-${child.id}-${m.id}`}>
                    Worksheet {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>
                </div>
              </div>
              {open && (
                <div className="px-6 pb-5 step-in">
                  <div className="rounded-2xl border border-[#e9f9fd] p-4">
                    <p className="font-bold text-[#0c4a6e] mb-2">{m.worksheet.title}</p>
                    <WorksheetReview child={child} m={m} />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="p-5 border-t border-[#e9f9fd]">
        <Cheers child={child} />
        {!streak.activeThisWeek && (child.activity || []).length > 0 && !readOnly && <div className="mt-3"><StreakRescueButton child={child} /></div>}
        <PepTalkRecorder child={child} />
      </div>
    </div>
  );
};

const FamilyDashboard = () => {
  const { children, activeChildId, switchChild, followedChildren, following, refreshFollowing, syncing } = useProgress();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { hash } = useLocation();
  const totalBamboo = children.reduce((a, c) => a + (c.bamboo || 0), 0);
  const totalCerts = [...children, ...followedChildren].reduce((a, c) => a + modules.filter((m) => progressPercent(c, m.id) === 100).length, 0);

  // Deep link from the header cheer badge: /family#child-<id>
  useEffect(() => {
    if (!hash || !children.length) return;
    const t = setTimeout(() => {
      const el = document.getElementById(hash.slice(1));
      if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 100, behavior: "smooth" });
    }, 250);
    return () => clearTimeout(t);
  }, [hash, children.length]);

  const followOnly = children.length === 0 && followedChildren.length > 0;
  const unfollow = async (f) => {
    if (!window.confirm(`Stop following ${f.owner.name}'s family?`)) return;
    await api.delete(`/following/${f.follow_id}`);
    toast(`Stopped following ${f.owner.name}'s family`);
    refreshFollowing();
  };

  return (
    <div data-testid="family-dashboard">
      <section className="relative bg-[#7d5fb2] overflow-hidden">
        <CloudDecor className="absolute right-[8%] top-8 hidden md:block cloud-drift" size={180} />
        <div className="relative max-w-6xl mx-auto px-6 pt-12 pb-24">
          <p className="text-[#f3c932] font-bold uppercase tracking-widest text-sm">{followOnly ? "Family & friends" : "Parents"}</p>
          <h1 className="text-white font-bold text-4xl sm:text-5xl mt-2" style={{ fontFamily: "Literata, serif" }}>{followOnly ? "Children you follow" : "Family dashboard"}</h1>
          <p className="text-white/90 text-lg mt-3 max-w-2xl">{followOnly ? "Follow their lessons, quiz scores and certificates - and send a cheer to keep them going." : "See which lessons each child finished, their quiz scores, and the worksheets to review together."}</p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            {followOnly ? (
              <span className="bg-white/15 text-white rounded-full px-4 py-2 font-bold text-sm" data-testid="hero-following-count">Following {followedChildren.length} child{followedChildren.length === 1 ? "" : "ren"}</span>
            ) : (
              <>
                <span className="bg-white/15 text-white rounded-full px-4 py-2 font-bold text-sm">{children.length} child{children.length === 1 ? "" : "ren"}</span>
                <span className="bg-white/15 text-white rounded-full px-4 py-2 font-bold text-sm">{totalBamboo} bamboo total</span>
              </>
            )}
            <span className={`rounded-full px-4 py-2 font-bold text-sm inline-flex items-center gap-1 ${user ? "bg-[#63c79a] text-white" : "bg-[#f3c932] text-[#5a4600]"}`}>
              {user ? <><Cloud size={14} /> Synced to {user.email}</> : <><CloudOff size={14} /> Saved on this device only</>}
            </span>
            <Link to="/certificates" className="rounded-full px-4 py-2 font-bold text-sm bg-[#f3c932] text-[#5a4600] inline-flex items-center gap-1 hover:bg-[#ffd84d]" data-testid="dashboard-gallery-link"><Award size={14} /> {totalCerts} certificate{totalCerts === 1 ? "" : "s"} - view wall</Link>
            {user && children.filter((c) => c.ownerId).length >= 2 && <Link to={`/family?story=1`} className="rounded-full px-4 py-2 font-bold text-sm bg-white/90 text-[#7d5fb2] inline-flex items-center gap-1 hover:bg-white" data-testid="family-story-button"><Sparkles size={14} /> Play the {new Date().getFullYear() - 1} family story</Link>}
            {user && <Link to="/family?tour=1" className="rounded-full px-4 py-2 font-bold text-sm bg-white/60 text-[#0c4a6e] inline-flex items-center gap-1 hover:bg-white" data-testid="dashboard-tour-link"><Eye size={14} /> Take the tour</Link>}
            {!followOnly && <Link to="/panda?new=1" className="btn-pill bg-[#e5356f] hover:bg-[#c92a5e] text-white inline-flex items-center gap-1 ml-auto" data-testid="dashboard-add-child"><Plus size={18} /> Add a child</Link>}
          </div>
        </div>
        <CurveBottom color="#f7fdfe" />
      </section>

      <section className="bg-[#f7fdfe] pb-20">
        <div className="max-w-6xl mx-auto px-6 space-y-8">
          <QueueBanner />
          <Leaderboard children={children} />
          {user && <FamilyYearStory children={children.filter((c) => c.ownerId)} />}
          {user && children.length > 0 && <PhotoWall />}
          {user && <ReminderSettings />}
          {user && <SoundSettings />}
          {user && <DataExport />}
          {user && <DeleteAccount />}
          {!user && children.length > 0 && (
            <div className="bg-[#fff8e1] border-2 border-[#f3c932] rounded-2xl p-4 flex flex-col sm:flex-row items-center gap-3 text-[#5a4600]">
              <p className="flex-1 text-sm">These children live only in this browser. <span className="font-bold">Create a free account</span> to keep the whole family's progress on every device.</p>
              <Link to="/login" state={{ tab: "register" }} className="btn-pill bg-[#1b8bc9] hover:bg-[#156f9f] text-white text-sm">Sign up</Link>
            </div>
          )}
          {children.length === 0 ? (
            (syncing || authLoading) ? (
              <div className="bg-white rounded-3xl shadow-md p-12 text-center" data-testid="family-loading">
                <Panda size={110} accessory="none" scarf="#1b8bc9" animate className="mx-auto" />
                <p className="text-gray-600 mt-4 font-bold">Loading your family...</p>
              </div>
            ) : followedChildren.length === 0 && (
              <div className="bg-white rounded-3xl shadow-md p-12 text-center">
                <Panda size={140} accessory="hat" scarf="#e5356f" animate className="mx-auto" />
                <h2 className="text-2xl font-bold text-[#0c4a6e] mt-4" style={{ fontFamily: "Literata, serif" }}>No children yet</h2>
                <p className="text-gray-600 mt-2">Add your first child to start tracking lessons, quizzes and certificates.</p>
                <Link to="/panda?new=1" className="mt-6 btn-pill-lg bg-[#e5356f] hover:bg-[#c92a5e] text-white inline-flex items-center gap-2"><Plus size={20} /> Add a child</Link>
              </div>
            )
          ) : (
            children.map((c) => (
              <ChildCard key={c.id} child={c} siblings={user ? children.filter((o) => o.id !== c.id && o.ownerId) : []} isActive={c.id === activeChildId} onActivate={() => { switchChild(c.id); toast.success(`${c.panda.name} is learning now`); navigate("/lessons"); }} />
            ))
          )}

          {user && children.length > 0 && <InviteManager children={children} />}

          {following.length > 0 && (
            <div className="space-y-6" data-testid="following-section">
              <div className="flex items-center gap-3 pt-4">
                <span className="w-10 h-10 rounded-2xl bg-[#f3e8ff] text-[#7d5fb2] flex items-center justify-center"><Eye size={20} /></span>
                <div>
                  <h2 className="text-2xl font-bold text-[#0c4a6e]" style={{ fontFamily: "Literata, serif" }}>Children you follow</h2>
                  <p className="text-sm text-gray-600">Read-only. Cheer them on - and share their certificates!</p>
                </div>
              </div>
              {following.map((f) => (
                <div key={f.follow_id} className="space-y-4">
                  <div className="flex items-center justify-between">
                    <p className="font-bold text-[#7d5fb2]">{f.owner.name}'s family</p>
                    <button onClick={() => unfollow(f)} className="text-xs font-bold text-gray-400 hover:text-[#e5356f]" data-testid={`unfollow-${f.follow_id}`}>Stop following</button>
                  </div>
                  {f.children.length === 0 && <p className="text-sm text-gray-400 italic">No children shared yet.</p>}
                  {f.children.map((c) => <ChildCard key={c.id} child={followedChildren.find((fc) => fc.id === c.id) || { ...c, ownerId: c.owner_id, lessonDay: c.lesson_day ?? null, ownerName: f.owner.name, readOnly: true }} readOnly />)}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default FamilyDashboard;
